import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { listLookup } from '../../api/masterData';

interface CommResultSummary {
  total: number;
  sent: number;
  failed: number;
}

interface BulkCommDialogProps {
  open: boolean;
  studentCount: number;
  errorMessage: string | null;
  resultSummary: CommResultSummary | null;
  onClose: () => void;
}

interface BulkEmailDialogProps extends BulkCommDialogProps {
  onSubmit: (input: { templateId?: number; subject?: string; body?: string }) => Promise<void>;
}

export const BulkEmailDialog = ({
  open,
  studentCount,
  errorMessage,
  resultSummary,
  onClose,
  onSubmit,
}: BulkEmailDialogProps) => {
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ['lookup', '/master-data/email-templates'],
    queryFn: () => listLookup('/master-data/email-templates', { activeOnly: true }),
    enabled: open,
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(
        templateId ? { templateId } : { subject: subject || undefined, body: body || undefined },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Email {studentCount} student(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {resultSummary && (
            <Alert severity={resultSummary.failed === 0 ? 'success' : 'warning'}>
              Sent {resultSummary.sent}/{resultSummary.total} ({resultSummary.failed} failed)
            </Alert>
          )}
          <TextField
            select
            label="Template"
            fullWidth
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Free text</em>
            </MenuItem>
            {templatesQuery.data?.map((t) => (
              <MenuItem key={t.id as number} value={t.id as number}>
                {String(t.name)}
              </MenuItem>
            ))}
          </TextField>
          {!templateId && (
            <>
              <TextField label="Subject" fullWidth value={subject} onChange={(e) => setSubject(e.target.value)} />
              <TextField
                label="Message"
                multiline
                minRows={3}
                fullWidth
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </>
          )}
          <Typography variant="caption" color="text.secondary">
            Supports {'{{firstName}}'}, {'{{lastName}}'}, {'{{studentNumber}}'}, {'{{university}}'} and{' '}
            {'{{departureDate}}'} placeholders.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          disabled={(!templateId && !subject.trim() && !body.trim()) || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface BulkWhatsAppDialogProps extends BulkCommDialogProps {
  onSubmit: (input: { templateId?: number; body?: string }) => Promise<void>;
}

export const BulkWhatsAppDialog = ({
  open,
  studentCount,
  errorMessage,
  resultSummary,
  onClose,
  onSubmit,
}: BulkWhatsAppDialogProps) => {
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ['lookup', '/master-data/whatsapp-templates'],
    queryFn: () => listLookup('/master-data/whatsapp-templates', { activeOnly: true }),
    enabled: open,
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ templateId: templateId || undefined, body: templateId ? undefined : body });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>WhatsApp {studentCount} student(s)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {resultSummary && (
            <Alert severity={resultSummary.failed === 0 ? 'success' : 'warning'}>
              Sent {resultSummary.sent}/{resultSummary.total} ({resultSummary.failed} failed)
            </Alert>
          )}
          <TextField
            select
            label="Template"
            fullWidth
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Free text</em>
            </MenuItem>
            {templatesQuery.data?.map((t) => (
              <MenuItem key={t.id as number} value={t.id as number}>
                {String(t.name)}
              </MenuItem>
            ))}
          </TextField>
          {!templateId && (
            <TextField
              label="Message"
              multiline
              minRows={2}
              fullWidth
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Supports {'{{firstName}}'}, {'{{lastName}}'}, {'{{studentNumber}}'}, {'{{university}}'} and{' '}
            {'{{departureDate}}'} placeholders.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={(!templateId && !body.trim()) || submitting} onClick={handleSubmit}>
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
